<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('scale.scale_houses.title')"
      :show-add="canEdit"
      :show-filter="false"
      @columns="openColumnChooser"
      @export="exportGrid"
      @add="handleAddClick"
    />

    <DxDataGrid
      :data-source="scaleHouseDataSurce"
      :remote-operations="gridRemoteOperations"
      :showBorders="true"
      :allowColumnReordering="true"
      :allowColumnResizing="true"
      :row-alternation-enabled="true"
      :searchPanel="{
        visible: false,
        width: 240,
        placeholder: $t('common.search'),
      }"
      :ref="gridRefKey"
      :element-attr="gridAttributes"
      @editing-start="onEditingStart"
      @init-new-row="onInitNewRow"
      key-expr="uuid"
    >
      <DxColumn
        dataField="name"
        :caption="$t('common.name')"
        :defaultFilterOperation="'contains'"
        :filter-operations="['contains']"
        :allowFiltering="true"
        :allowHeaderFiltering="true"
        :sortOrder="'asc'"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn dataField="prefix" :caption="$t('common.prefix')" :filter-operations="['contains']">
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <template #prefixEditTemplate="{ data }">
        <DxTextBox v-model="data.data.prefix_temp" :showClearButton="true" @input="(e) => onPrefixChange(data, e)">
          <DxValidator>
            <DxValidationRule type="required" :message="$t('validation.field_is_required')" />
          </DxValidator>
        </DxTextBox>
      </template>
      <DxColumn
        dataField="round_quantity_on_kg"
        :caption="$t('scale.scale_houses.round_quantity_on_kg')"
        dataType="number"
        :lookup="{
          dataSource: [1, 5], // The selectable values
          valueExpr: null, // Use the raw value directly
          displayExpr: null, // Use the raw value directly
        }"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn
        :caption="$t('scale.scale_houses.document_preview')"
        dataField="document_preview_temp"
        cell-template="documentPreviewTemplate"
        dataType="string"
        :visible="true"
        :allowFiltering="false"
        :allowSorting="false"
        :allowEditing="false"
      />
      <template #documentPreviewTemplate="{ data }">
        <span>
          {{ generateDocumentNumberPreview(data.data) }}
        </span>
      </template>
      <template #documentPreviewEditTemplate="{ data }">
        <DxTextBox v-model="data.data.document_preview_temp" :disabled="true"> </DxTextBox>
      </template>
      <DxColumn
        dataField="user_uuids"
        cell-template="userViewTemplate"
        :caption="$t('common.users')"
        edit-cell-template="userSelectorTemplate"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :lookup="{
          dataSource: users,
          valueExpr: 'uuid',
          displayExpr: (user) => `${user.first_name} ${user.last_name}`,
        }"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>

      <template #userViewTemplate="{ data }">
        <span>
          {{ mapUserNames(data.data.user_uuids) }}
        </span>
      </template>

      <template #userSelectorTemplate="{ data }">
        <DxTagBox
          :data-source="users"
          value-expr="uuid"
          :display-expr="(user) => `${user.first_name} ${user.last_name}`"
          :value="data.data.user_uuids"
          :showClearButton="true"
          :searchEnabled="true"
          :applyValueMode="'useButtons'"
          :showSelectionControls="true"
          :selectAllMode="'allPages'"
          :selectAllText="$t('select_all')"
          :noDataText="$t('common.no_data_found')"
          @value-changed="(e) => updateRowUsers(data, e.value)"
        />
      </template>

      <DxColumn
        dataField="updated_at"
        :caption="$t('common.updated_at')"
        dataType="date"
        :visible="true"
        :allowFiltering="true"
        :allowSorting="true"
        :allowEditing="false"
        :filter-operations="['contains']"
        :format="getDxGridDefaultDateFormat + ' HH:mm'"
        :editorOptions="{ displayFormat: getDxGridDefaultDateFormat + ' HH:mm', disabled: true }"
      >
      </DxColumn>
      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
        :editorOptions="{ disabled: true }"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs[gridRefKey]"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
        />
      </template>
      <DxEditing mode="form" :allowUpdating="false" :allowAdding="false" :allowDeleting="false" :confirm-delete="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('scale.scale_houses.title_edit'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :save-row-changes="$t('common.save')"
          :cancel-row-changes="$t('common.close')"
          :addRow="$t('common.add_new')"
          :editRow="$t('common.edit')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxPaging :total="3500" :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxHeaderFilter :visible="true" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
      <DxColumnChooser :enabled="false" mode="select" />
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxEditing,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxTexts,
  DxButton,
  DxPopup,
  DxToolbar,
  DxItem,
  DxRequiredRule,
  DxColumnChooser,
} from "devextreme-vue/data-grid";
import DxTagBox from "devextreme-vue/tag-box";
import { canView, canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import Forbidden from "~/pages/errors/Forbidden.vue";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import DxTextBox from "devextreme-vue/text-box";
import DxValidator, { DxValidationRule } from "devextreme-vue/validator";
import notify from "devextreme/ui/notify";
import { format } from "date-fns";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";

export default {
  name: "ScaleHouses",
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxTexts,
    DxButton,
    DxPaging,
    DxPager,
    DxPopup,
    DxToolbar,
    DxItem,
    Forbidden,
    DxTagBox,
    DxRequiredRule,
    DxColumnChooser,
    DxTextBox,
    DxValidator,
    DxValidationRule,
    GridPanelHeader,
    DxActionMenu,
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid scale-house-grid",
      },
      gridRefKey: "scaleHouseGrid",
      scaleHouseDataSurce: new DataSource({
        store: new CustomStore({
          key: "uuid",
          load: this.load,
          insert: this.insert,
          update: this.update,
          remove: this.remove,
        }),
      }),
      gridRemoteOperations: {
        paging: false,
        filtering: false,
        sorting: false,
      },
      pageSizes: [15, 50, 100],
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
      scaleHouses: "scale/scaleHouses",
      users: "user/users",
    }),
    config() {
      return {
        permission: permissions.SCALE_HOUSES,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    dataGridInstance() {
      return this.$refs[this.gridRefKey].instance;
    },
  },
  methods: {
    ...mapActions({
      getScaleHouses: "scale/getScaleHouses",
      addScaleHouse: "scale/createScaleHouse",
      updateScaleHouse: "scale/updateScaleHouse",
      deleteScaleHouse: "scale/deleteScaleHouse",
      getUsers: "user/getUsers",
    }),
    async load(loadOptions) {
      let dataSource = {
        data: [],
        totalCount: 0,
      };

      let payload = {
        limit: 999999,
      };

      try {
        await this.getScaleHouses(payload).then((response) => {
          dataSource.data = response.data;
          dataSource.totalCount = 1;
        });

        return dataSource;
      } catch (error) {
        console.error("Error fetching scale houses:", error);
        throw error;
      }
    },
    async insert(obj) {
      try {
        await this.addScaleHouse({
          name: obj.name,
          prefix: obj.prefix,
          round_quantity_on_kg: obj.round_quantity_on_kg,
          user_uuids: obj.user_uuids,
        }).then((response) => {
          this.notifyMsg(response);
        });
      } catch (error) {
        console.error("Error adding scale house:", error);
        throw error;
      }
    },
    async update(key, obj) {
      try {
        await this.updateScaleHouse({
          uuid: key,
          name: obj.name,
          prefix: obj.prefix,
          round_quantity_on_kg: obj.round_quantity_on_kg,
          user_uuids: obj.user_uuids,
        }).then((response) => {
          this.notifyMsg(response);
        });
      } catch (error) {
        console.error("Error updating scale house:", error);
        throw error;
      }
    },
    async remove(key) {
      try {
        await this.deleteScaleHouse({ uuid: key }).then((response) => {
          this.notifyMsg(response);
        });
      } catch (error) {
        console.error("Error deleting scale house:", error);
        throw error;
      }
    },
    openColumnChooser() {
      const instance = this.dataGridInstance;
      if (instance) {
        instance.showColumnChooser();
      }
    },
    exportGrid() {
      const instance = this.dataGridInstance;
      if (!instance) return;

      this.onExporting({
        component: instance,
        format: "xlsx",
        fileName: "ScaleHouses_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss"),
      });
    },
    handleAddClick() {
      const instance = this.dataGridInstance;
      if (instance) {
        instance.addRow();
      }
    },
    onExporting(e) {
      e.fileName = "ScaleHouses_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("ScaleHouses");
        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          customizeCell: ({ gridCell, excelCell }) => {
            // Skip processing for non-data rows (headers, summaries, etc.)
            if (!gridCell?.data) {
              return;
            }

            // Generate document_preview_temp value in export
            if (gridCell?.column?.dataField === "document_preview_temp") {
              excelCell.value = this.generateDocumentNumberPreview(gridCell?.data);
            }

            // Convert user_uuids to user names
            if (gridCell?.column?.dataField === "user_uuids") {
              excelCell.value = this.mapUserNames(gridCell?.data?.user_uuids);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    updateRowUsers(data, value) {
      if (JSON.stringify(data.data.user_uuids) !== JSON.stringify(value)) {
        data.data.user_uuids = value;
        data.setValue(value);
      }
    },
    mapUserNames(uuids) {
      if (!uuids || !Array.isArray(uuids) || !this.users) {
        return "";
      }

      return uuids
        .map((uuid) => {
          const user = this.users.find((user) => user.uuid === uuid);
          return user ? `${user.first_name} ${user.last_name}` : "";
        })
        .filter((name) => name)
        .join(", ");
    },
    onEditingStart(e) {
      // Initialize prefix_temp with the value of prefix when editing starts
      e.data.prefix_temp = e.data.prefix || ""; // Default prefix if none is provided
      e.data.document_preview_temp = this.generateDocumentNumberPreview(e.data);
    },
    onInitNewRow(e) {
      // Initialize prefix_temp with an empty string when a new row is created
      e.data.prefix_temp = ""; // Default prefix if none is provided
      e.data.document_preview_temp = this.generateDocumentNumberPreview(e.data);
    },
    onPrefixChange(data, e) {
      var value = e.event.target.value;
      data.data.prefix_temp = value;
      data.setValue(value);
      // Regenerate document_preview_temp
      data.data.document_preview_temp = this.generateDocumentNumberPreview(data.data, value);
    },
    generateDocumentNumberPreview(rowData, prefix_temp = null) {
      const prefix = prefix_temp || rowData.prefix || "{prefix}"; // Default prefix if none is provided
      const currentYear = new Date().getFullYear().toString().slice(-2);
      const sequenceNumber = rowData.sequence || 1; // Default sequence number if none is provided

      // Format the sequence number as a zero-padded string (e.g., 00000001)
      const formattedSequence = sequenceNumber.toString().padStart(8, "0");

      // Combine the prefix, year, and sequence number
      return `${prefix}${currentYear}/${formattedSequence}`;
    },
    notifyMsg(response) {
      notify({
        message: response.message ?? "",
        type: response.status == "ok" ? "success" : "error",
        displayTime: 20500,
        width: "auto",
        position: { at: "top center", my: "top center" },
      });
    },
  },
  mounted() {
    this.getUsers({
      enablePagination: 0,
      limit: 1000,
    });
  },
};
</script>
